import {
  FetchPolicy,
  gql,
  NetworkStatus,
  useApolloClient,
  useQuery,
} from '@apollo/client';
import { useCallback } from 'react';
import {
  SearchArticleCountQuery,
  SearchArticleCountQueryVariables,
  SearchArticlesQuery,
  SearchArticlesQueryVariables,
} from '../../__generated__/queries-web';

export const SEARCH_ARTICLES = gql`
  query SearchArticles(
    $where: ArticleFilterSearchInput
    $query: NonEmptyTrimmedString
    $like: ArticleLikeInput
    $first: UnsignedInt!
    $skip: UnsignedInt!
    $orderBy: [ArticleOrderingSearchInput!]
  ) {
    searchArticles(
      first: $first
      where: $where
      query: $query
      like: $like
      skip: $skip
      orderBy: $orderBy
    ) {
      id
      title
      status
      body
      createdAt
      publishedAt
      unpublishAt
      editedAt
      updatedAt
      brandKey
      format
      medias
      pushCount
      refererCount
      live {
        postCount
      }
      categories(first: 2, orderBy: order_ASC) {
        order
        category {
          id
          title
        }
      }
      channels(first: 1) {
        order
        category {
          id
          title
        }
      }
      creator {
        name
      }
      urls(first: 1, orderBy: order_ASC) {
        origin
        path
      }
      qualifiers(first: 100) {
        order
        qualifier {
          id
          title
        }
      }
      lockedAt
      lockedBy {
        id
        name
        email
        avatarUrl
      }
      canonical
      events(first: 10) {
        category {
          id
          title
        }
        order
      }
      histories(first: 100, orderBy: createdAt_ASC) {
        createdAt
        user {
          ... on OneUser {
            name
          }
        }
      }
      qualifiers(first: 100) {
        qualifier {
          id
          title
        }
      }
      tags(first: 20, orderBy: order_ASC) {
        order
        tag {
          id
          relationId
          title
          type
        }
      }
      authors(first: 1) {
        author {
          id
          name
          avatarUrl
        }
      }
      newsletterFlashSenderHistories {
        createdAt
      }
    }
  }
`;

const SEARCH_ARTICLE_COUNT = gql`
  query SearchArticleCount(
    $where: ArticleFilterSearchInput
    $query: NonEmptyTrimmedString
  ) {
    searchArticleCount(where: $where, query: $query)
  }
`;

export const EXPORT_SEARCH_ARTICLES = gql`
  query ExportSearchArticles(
    $where: ArticleFilterSearchInput
    $query: NonEmptyTrimmedString
    $like: ArticleLikeInput
    $first: UnsignedInt!
    $skip: UnsignedInt!
    $orderBy: [ArticleOrderingSearchInput!]
  ) {
    searchArticles(
      first: $first
      where: $where
      query: $query
      like: $like
      skip: $skip
      orderBy: $orderBy
    ) {
      id
      title
      status
      publishedAt
      brandKey
      format
      categories(first: 100, orderBy: order_ASC) {
        category {
          id
          title
        }
      }
      urls(first: 1, orderBy: order_ASC) {
        origin
        path
      }
      qualifiers(first: 100) {
        qualifier {
          id
          title
        }
      }
      tags(first: 100, orderBy: order_ASC) {
        tag {
          id
          title
        }
      }
    }
  }
`;

export const useSearchArticleCount = (
  variables: SearchArticleCountQueryVariables,
  skip?: boolean,
) =>
  useQuery<SearchArticleCountQuery, SearchArticleCountQueryVariables>(
    SEARCH_ARTICLE_COUNT,
    { variables, skip },
  );

export const useSearchArticlesGetter = () => {
  const client = useApolloClient();

  return (variables: SearchArticlesQueryVariables) =>
    client.query<SearchArticlesQuery, SearchArticlesQueryVariables>({
      query: SEARCH_ARTICLES,
      variables,
      fetchPolicy: 'network-only',
    });
};

export const useSearchArticles = (
  variables: SearchArticlesQueryVariables,
  skip?: boolean,
) => {
  const { data, ...rest } = useQuery<
    SearchArticlesQuery,
    SearchArticlesQueryVariables
  >(SEARCH_ARTICLES, {
    notifyOnNetworkStatusChange: true,
    skip,
    variables,
    fetchPolicy: 'network-only',
  });

  return {
    data: data?.searchArticles && { articles: data.searchArticles },
    ...rest,
  };
};

export const initialApolloState = {
  data: null,
  loading: false,
  stale: false,
  networkStatus: NetworkStatus.ready,
};

export const useSearchArticleCountGetter = () => {
  const client = useApolloClient();
  return useCallback(
    (
      variables: SearchArticleCountQueryVariables,
      skip?: boolean,
      fetchPolicy: FetchPolicy = 'network-only',
    ) =>
      skip
        ? Promise.resolve(initialApolloState)
        : client.query<
            SearchArticleCountQuery,
            SearchArticleCountQueryVariables
          >({
            query: SEARCH_ARTICLE_COUNT,
            variables,
            fetchPolicy,
          }),
    [client],
  );
};
